import { multiplayer } from "../multiplayer";

// Dispatch synchronously from the tap so touch browsers can open the keyboard.
export function openOnlineChat(): void {
  window.dispatchEvent(new Event("mergeborn-open-chat"));
}

export function requestLeaveMatch(): void {
  if (!multiplayer.match || !window.confirm("Are you sure you want to quit this game?")) return;
  multiplayer.leave();
  window.dispatchEvent(new Event("mergeborn-leave-match"));
}

// This overlay survives scene changes, so chat and connection notices don't disappear between rounds.
export function installOnlineOverlay(): void {
  const root = document.createElement("div"); root.className = "online-overlay"; root.hidden = true;
  root.innerHTML = `<header class="online-scoreboard"><div class="online-player"><span class="online-player-name"></span><strong>0</strong></div><div class="online-match-status" role="status"></div><div class="online-player online-rival"><span class="online-player-name"></span><strong>0</strong></div></header><div class="online-chat-messages" role="log" aria-live="polite"></div><form class="online-chat-form" hidden><input maxlength="160" autocomplete="off" enterkeyhint="send" aria-label="Chat message" placeholder="Say something…"><div class="online-chat-actions"><button type="submit" aria-label="Send message">➤</button><button type="button" class="online-chat-close" aria-label="Close chat">×</button></div></form><div class="online-chat-hint">Enter to chat</div>`;
  document.querySelector("#app-shell")?.append(root);
  const names = root.querySelectorAll<HTMLElement>(".online-player-name");
  const scores = root.querySelectorAll<HTMLElement>("strong");
  const status = root.querySelector<HTMLElement>(".online-match-status")!;
  const form = root.querySelector<HTMLFormElement>("form")!;
  const inputTemplate = root.querySelector<HTMLInputElement>("input")!;
  inputTemplate.remove();
  let input: HTMLInputElement | null = null;
  let draft = "";
  const messages = root.querySelector<HTMLElement>(".online-chat-messages")!;
  const timers = new Map<HTMLElement, ReturnType<typeof setTimeout>>();
  const remove = (bubble: HTMLElement) => { clearTimeout(timers.get(bubble)); timers.delete(bubble); bubble.remove(); };
  const showMessage = (message: { name: string; text: string }) => {
    if (root.hidden) return;
    const bubble = document.createElement("div"); bubble.className = "online-chat-message";
    const author = document.createElement("strong"); author.textContent = message.name;
    const body = document.createElement("span"); body.textContent = message.text;
    bubble.append(author, body); messages.prepend(bubble);
    // New messages appear now, rather than waiting behind another message's fade.
    while (messages.childElementCount > 5) remove(messages.lastElementChild as HTMLElement);
    timers.set(bubble, setTimeout(() => {
      bubble.classList.add("is-fading");
      timers.set(bubble, setTimeout(() => remove(bubble), 650));
    }, 3500));
  };
  const closeChat = () => {
    if (input) {
      draft = input.value;
      input.blur();
      // Release the editor instead of leaving a hidden typing control during play.
      input.remove();
      input = null;
    }
    form.hidden = true;
  };
  const openChat = () => {
    if (!multiplayer.match) return;
    if (!input) {
      input = inputTemplate.cloneNode() as HTMLInputElement;
      input.value = draft;
      form.prepend(input);
    }
    form.hidden = false;
    input.focus();
  };
  window.addEventListener("mergeborn-open-chat", openChat);
  root.querySelector(".online-chat-close")!.addEventListener("click", closeChat);
  form.addEventListener("submit", event => {
    event.preventDefault();
    if (!input) return;
    const text = input.value.trim();
    if (text) multiplayer.chat(text);
    input.value = "";
    closeChat();
  });
  form.addEventListener("keydown", event => { event.stopPropagation(); if (event.key === "Escape") { event.preventDefault(); closeChat(); } });
  document.addEventListener("keydown", event => {
    if (!multiplayer.match || event.isComposing || event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
    const target = event.target;
    if (target instanceof HTMLElement && (target.isContentEditable || target.matches("input, textarea, select, button"))) return;
    if (event.key === "Enter") { event.preventDefault(); event.stopImmediatePropagation(); openChat(); }
  }, true);
  const refresh = () => {
    const match = multiplayer.match; root.hidden = !match;
    if (!match) { for (const bubble of timers.keys()) remove(bubble); closeChat(); draft = ""; return; }
    const ownOnLeft = match.ownSide === "opponent";
    const members = ownOnLeft ? [match.own, match.other] : [match.other, match.own];
    members.forEach((member, i) => {
      names[i].textContent = member.name; scores[i].textContent = String(member.score);
      names[i].parentElement!.classList.toggle("is-you", member.id === match.own.id);
      names[i].title = member.id === match.own.id ? "Your team" : member.name;
    });
    if (multiplayer.status !== "connected") status.textContent = "Reconnecting…";
    else if (!match.other.connected) status.textContent = "Opponent reconnecting…";
    else if (match.own.run?.phase === "battle") status.textContent = multiplayer.battleCountdownRemaining > 0 ? "Both teams ready · Starting battle…" : "Battle";
    else if (match.own.ready) status.textContent = `Waiting for ${match.other.name}…`;
    else if (!match.own.run) status.textContent = "Choose your elemental";
    else if (match.own.run.phase === "reward") status.textContent = "Choose your gift";
    else if (match.other.ready) status.textContent = `Opponent ready · Your team is on the ${ownOnLeft ? "left" : "right"}`;
    else status.textContent = `Arrange your team on the ${ownOnLeft ? "left" : "right"}`;
  };
  multiplayer.subscribe(event => {
    refresh();
    if (event.type === "chat") showMessage(event);
  });
}
